import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, File, X, Download, Trash2, FileText, Key, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['pem', 'key', 'crt', 'cer'].includes(ext)) return <Key className="w-5 h-5" />;
  if (['ppk', 'p12', 'pfx'].includes(ext)) return <Lock className="w-5 h-5" />;
  return <FileText className="w-5 h-5" />;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default function FileUpload({ credentialId, attachments, onUpdate, canEdit }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/credentials/${credentialId}/attachments`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('File uploaded successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/credentials/${credentialId}/attachments/${attachmentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/credentials/${credentialId}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Attachment deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-text-muted">Attachments</label>
        {canEdit && (
          <label className="btn-outline px-3 py-1 text-sm cursor-pointer" data-testid="btn-upload-file">
            <input
              type="file"
              className="hidden"
              onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
              disabled={uploading}
            />
            <Upload size={16} className="inline mr-1" />
            Upload File
          </label>
        )}
      </div>

      {canEdit && (
        <div
          className={`border-2 border-dashed p-6 text-center transition-colors ${
            dragActive ? 'border-primary bg-primary/5' : 'border-gray-300'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          data-testid="file-drop-zone"
        >
          <File className="w-12 h-12 text-text-muted mx-auto mb-2" />
          <p className="text-text-secondary text-sm">
            {uploading ? 'Uploading...' : 'Drag & drop files here or click Upload button'}
          </p>
          <p className="text-text-muted text-xs mt-1">SSL certs, PEM, PPK, keychains, etc.</p>
        </div>
      )}

      {attachments && attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="bg-white border border-gray-200 p-4 flex items-center justify-between hover:border-black transition-colors"
              data-testid={`attachment-${attachment.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 text-primary">
                  {getFileIcon(attachment.filename)}
                </div>
                <div>
                  <p className="text-text-primary font-medium text-sm">{attachment.filename}</p>
                  <p className="text-text-muted text-xs">
                    {formatFileSize(attachment.size)} • {new Date(attachment.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownload(attachment.id, attachment.filename)}
                  className="text-primary hover:text-primary-hover p-2"
                  title="Download"
                  data-testid={`btn-download-${attachment.id}`}
                >
                  <Download size={18} />
                </button>
                {canEdit && (
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    className="text-accent hover:text-accent/80 p-2"
                    title="Delete"
                    data-testid={`btn-delete-${attachment.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {(!attachments || attachments.length === 0) && !canEdit && (
        <p className="text-text-muted text-sm text-center py-4">No attachments</p>
      )}
    </div>
  );
}